import React, { useMemo } from 'react';
import { AnalysisResult } from '../../contracts/scoring';
import { MasterSessionVisualizer } from '../../components/MasterSessionVisualizer';

type MetricCard = { label: string; value: string; hint?: string };

const safeNum = (n: any) => {
  const x = Number(n);
  return Number.isFinite(x) ? x : null;
};

const fmt = (n: any, decimals = 1) => {
  const x = safeNum(n);
  if (x === null) return '—';
  return x.toFixed(decimals);
};

const pct = (n: any, decimals = 1) => {
  const x = safeNum(n);
  if (x === null) return '—';
  return (x * 100).toFixed(decimals) + '%';
};

export const AudioTab: React.FC<{ result: AnalysisResult }> = ({ result }) => {
  const dbg = (result.session.metrics.debug || {}) as any;

  const confidence = result.session.quality_score || 0;

  // ✅ Source unique pour le hash (évite divergence audit_trail vs truth_report)
  const analysisHash =
    result.session.truth_report?.tech_pack?.analysis_hash ||
    (result.audit_trail as any)?.analysis_hash ||
    '';

  // ✅ “Cloud certified” si hash existe ET n’est pas local_
  const isCertified =
    typeof analysisHash === 'string' &&
    analysisHash.length > 0 &&
    !analysisHash.startsWith('local_');

  // ✅ Mesures détectées (safe)
  const measuredCards: MetricCard[] = useMemo(() => {
    const cards: MetricCard[] = [];

    // RMS dB (déjà en dB, donc fmt direct)
    cards.push({
      label: 'RMS Power',
      value: dbg.rms_db != null ? `${fmt(dbg.rms_db, 1)} dB` : '—',
      hint: "Intensité électrique mesurée",
    });

    // peakedness, stability, clip_ratio sont probablement en 0..1
    cards.push({
      label: 'Peak Sample',
      value: dbg.peakedness != null ? pct(dbg.peakedness, 1) : '—',
      hint: "Crête d'amplitude",
    });

    cards.push({
      label: 'Signal Stability',
      value: dbg.stability != null ? pct(dbg.stability, 1) : '—',
      hint: "Cohérence temporelle du flux",
    });

    cards.push({
      label: 'Clipping Rate',
      value: dbg.clip_ratio != null ? pct(dbg.clip_ratio, 2) : '—',
      hint: "Saturation binaire détectée",
    });

    // Hash (preuve runtime)
    cards.push({
      label: 'Analysis hash',
      value: analysisHash ? (analysisHash.length > 18 ? `${analysisHash.slice(0, 18)}…` : analysisHash) : '—',
      hint: isCertified ? 'Signature Cloud' : 'Signature locale',
    });

    // Confidence
    cards.push({
      label: 'Confidence index',
      value: `${confidence}/100`,
      hint: 'Qualité du signal pour analyser',
    });

    return cards.slice(0, 6);
  }, [dbg, analysisHash, isCertified, confidence]);

  // ✅ Visu safe
  const refUrl = result.alignedUrls?.ref || '';
  const stuUrl = result.alignedUrls?.stu || '';

  const refEnv = result.rhythm?.refEnvelope || [];
  const stuEnv = result.rhythm?.stuEnvelope || [];

  const peaksRef = result.rhythm?.refPeaks || [];
  const peaksStu = result.rhythm?.stuPeaks || [];

  return (
    <div className="flex flex-col gap-10 animate-fade-in pb-12">
      {/* 1) SCORE + POURQUOI */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-4 bg-[#02080a] p-10 rounded-[48px] border border-white/10 shadow-2xl flex flex-col items-center text-center justify-center gap-6 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent"></div>
          <span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.4em] italic">Confidence Index</span>
          <div className="flex items-baseline gap-2">
            <span className={`text-8xl font-black italic tracking-tighter ${confidence > 75 ? 'text-emerald-400' : 'text-cyan-400'}`}>
              {confidence}
            </span>
            <span className="text-xl font-bold text-slate-700">/100</span>
          </div>

          <div
            className={`px-4 py-1.5 rounded-full border text-[9px] font-black uppercase tracking-widest ${
              isCertified ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 'bg-orange-500/10 border-orange-500/30 text-orange-400'
            }`}
          >
            {isCertified ? 'Signature Cloud Vérifiée 🛡️' : 'Validation Physique Locale'}
          </div>
        </div>

        <div className="lg:col-span-8 bg-black/40 p-10 rounded-[48px] border border-white/10 flex flex-col justify-center gap-8 shadow-inner relative overflow-hidden">
          <div className="absolute top-0 right-0 p-10 opacity-5 text-8xl font-black italic select-none">AUDIT</div>

          <div className="flex items-center gap-4 relative z-10">
            <div className="w-12 h-12 rounded-2xl bg-cyan-500/20 flex items-center justify-center text-2xl">🎛️</div>
            <h4 className="text-sm font-black text-white uppercase italic tracking-widest">Pourquoi ce score ?</h4>
          </div>

          <div className="bg-white/5 p-8 rounded-[40px] border border-white/5 relative z-10">
            <p className="text-sm text-slate-300 italic leading-relaxed">
              "L’Audio Lab évalue si le signal est exploitable et crédible : puissance (RMS), stabilité temporelle, absence de clipping,
              et cohérence du flux. L’objectif est de garantir que l’analyse repose sur une capture physique (No-SIM)."
            </p>
          </div>

          <div className="flex items-center gap-6 relative z-10">
            <span className="text-[9px] font-mono text-slate-500 truncate max-w-[320px]">
              {analysisHash || '—'}
            </span>
          </div>
        </div>
      </div>

      {/* 2) MESURES DÉTECTÉES (standard) */}
      <div className="bg-white/[0.02] border border-white/10 p-8 rounded-[48px] space-y-6">
        <h4 className="text-xs font-black text-slate-400 uppercase tracking-[0.3em]">Mesures détectées</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {measuredCards.map((m, i) => (
            <div key={i} className="bg-black/40 border border-white/10 rounded-2xl p-5">
              <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">{m.label}</div>
              <div className="text-2xl font-black italic text-white tabular-nums">{m.value}</div>
              {m.hint ? <div className="text-[9px] mt-2 italic text-slate-500">{m.hint}</div> : null}
            </div>
          ))}
        </div>
      </div>

      {/* 3) MODULE */}
      <div className="h-[650px] rounded-[56px] overflow-hidden border border-white/10 shadow-3xl bg-black">
        <MasterSessionVisualizer
          refUrl={refUrl}
          stuUrl={stuUrl}
          refEnvelope={refEnv}
          stuEnvelope={stuEnv}
          bpm={result.session.bpm}
          duration={result.session.duration}
          firstOnset={result.session.firstOnset}
          lagEstimateMs={result.session.lagMs}
          peaks={{ ref: peaksRef, stu: peaksStu }}
        />
      </div>
    </div>
  );
};
